/* eslint-disable consumerweb/no-indexof */
import { clientLogger } from '../../../../client/lib/logger';
import { getHost } from '../../../../lib/host';

/**
 * getSchemaOrgJSONLD()
 * Gets www.schema.org JSONLD for posting Structured Data: https://developers.google.com/search/docs/guides/intro-structured-data
 * @param imgUrlParsed {string} path to image to be associated with particular page by opengraph, google, and etc. (e.g.: https://www.paypalobjects.com/....png)
 * @param mainImageUrlParsed {string} path to image to be associated with main App Center page (e.g.: https://www.paypalobjects.com/....png)
 * @param description {string} meta/og description
 * @param title {string} structured data page name
 * @param country {string} 'us'
 * @param language {string} 'en'
 * @param detailsSlug {string} describes the tool page 'invoicing'
 * @param categorySlug {string} describes the category page 'acceptpayments'
 * @param openGraph {object} { titile: "App Store" }
 * @param mainTitle {string} 'PayPal Commerce Platform'
 * @param fullPath {string} https://www.paypal.com/us/business/appcenter/invoicing
 * getSchemaOrgJSONLD({
 *  imgUrl: "https://paypalobjects.com/.....svg",
 *  title: "App Center",
 *  language: "en",
 *  fullPath: "https://www.te-alm-17238701897587555.qa.paypal.com/merchantapps/appcenter",
 *  ... });
 * @returns {Object} Returns a javascript object to be converted into JSON and included in script tag for JSONLD
 */
export const getSchemaOrgJSONLD = ({
  mainImageUrlParsed,
  imgUrlParsed,
  title,
  mainTitle,
  language,
  country,
  categorySlug,
  detailsSlug,
  openGraph,
  fullPath,
  contextLocation
}) => {
  const { cdn: cdnHost } = contextLocation;

  return {
    '@context': 'http://schema.org',
    '@graph': [
      {
        '@type': 'Corporation',
        url: `${getHost(contextLocation)}/${country}/webapps/mpp/home`,
        name: 'PayPal Holdings inc',
        tickerSymbol: 'PYPL',
        logo: `https://${cdnHost}/webstatic/i/logo/rebrand/ppcom.png`,
        contactPoint: [
          {
            '@type': 'ContactPoint',
            url: `${getHost(contextLocation)}/cgi-bin/helpscr`,
            contactType: 'customer support'
          }
        ],
        sameAs: [
          'https://www.facebook.com/paypal/',
          'https://twitter.com/PayPal',
          'https://www.linkedin.com/company/paypal'
        ]
      },
      {
        '@type': 'WebSite',
        '@id': `${getHost(contextLocation)}/merchantapps/appcenter#website`,
        url: `${getHost(contextLocation)}/merchantapps/appcenter`,
        name: mainTitle,
        inLanguage: language
      },
      {
        '@type': 'Organization',
        name: 'PayPal Holdings Inc',
        url: `${getHost(contextLocation)}/${country}/webapps/mpp/home`,
        logo: `https://${cdnHost}/webstatic/i/logo/rebrand/ppcom.png`
      },
      {
        '@type': 'ImageObject',
        '@id': `${mainImageUrlParsed}#primaryimage`,
        url: mainImageUrlParsed,
        width: 700,
        height: 420
      },
      ...buildBreadcrumbs({
        categorySlug,
        detailsSlug,
        openGraph,
        fullPath,
        country,
        language,
        mainTitle,
        title,
        imgUrlParsed,
        contextLocation
      })
    ]
  };
};

export const parseStringifiedData = ({
  openGraph,
  parent,
  image,
  imgUrl,
  title,
  mainImageUrl
}) => {
  let parsedOpenGraph;
  let parsedParentOpenGraph;
  let mainImageUrlParsed;
  let imgUrlParsed;
  let extendedOpenGraph;
  try {
    parsedOpenGraph = openGraph ? parseData(openGraph)[0] : {};
    parsedParentOpenGraph = parent?.openGraph
      ? parseData(parent.openGraph)[0]
      : {};
    if (image) {
      imgUrlParsed = image;
    } else {
      imgUrlParsed = parseImage(imgUrl);
    }
    mainImageUrlParsed = parseImage(mainImageUrl);
    extendedOpenGraph = {
      ...parsedOpenGraph,
      parent: { ...parsedParentOpenGraph }
    };
    if (
      (!extendedOpenGraph.parent?.title ||
        extendedOpenGraph.parent?.title === '""') &&
      parent.title
    ) {
      extendedOpenGraph.parent.title = parent.title; // extract first string from for example "PayPal subscription | Automate repeat payments | PayPal US"
    }
    if (!extendedOpenGraph.title || extendedOpenGraph.title === '""') {
      extendedOpenGraph.title = title;
    }
  } catch (err) {
    if (typeof window !== 'undefined') {
      clientLogger.warn('invalid stringified data input to function', err);
    }
  }
  return { extendedOpenGraph, imgUrlParsed, mainImageUrlParsed };
};

export const getPageSeoData = (seo = {}, category, detail) => {
  let result = {};
  if (detail) {
    result = { ...seo[category]?.[detail] };
    result.parent = { ...seo[category]?.main };
  } else if (category) {
    result = seo[category]?.main;
  } else {
    result = seo.main;
  }
  result = { ...result, mainTitle: seo.main?.main };
  return result;
};

function buildChildListItems({
  categorySlug,
  title,
  detailsSlug,
  openGraph = {},
  contextLocation
}) {
  return [categorySlug, detailsSlug]
    .filter(i => i)
    .map((slug, index) => {
      const compedIndex = index + 2; // zero based index plus always 1 base item in list
      if (slug === detailsSlug) {
        return {
          '@type': 'ListItem',
          position: compedIndex,
          item: {
            '@type': 'WebPage',
            '@id': `${getHost(
              contextLocation
            )}/merchantapps/appcenter/${categorySlug}/${detailsSlug}`,
            name: openGraph.title
          }
        };
      }
      return {
        '@type': 'ListItem',
        position: compedIndex,
        item: {
          '@type': 'WebPage',
          '@id': `${getHost(
            contextLocation
          )}/merchantapps/appcenter/${categorySlug}`,
          name: openGraph.parent?.title || openGraph.title || title
        }
      };
    });
}

function buildBreadcrumbs({
  categorySlug,
  detailsSlug,
  openGraph = {},
  mainTitle,
  fullPath,
  language,
  imgUrlParsed,
  title,
  contextLocation
}) {
  if (!categorySlug && !detailsSlug) {
    return [];
  }

  return [
    {
      '@type': 'WebPage',
      '@id': `${fullPath}#webpage`,
      url: fullPath,
      inLanguage: language,
      name: openGraph.title ? openGraph.title : title,
      isPartOf: {
        '@id': `${getHost(contextLocation)}/merchantapps/appcenter#website`
      },
      primaryImageOfPage: { '@id': `${imgUrlParsed}#primaryimage` },
      breadcrumb: { '@id': `${fullPath}#breadcrumb` }
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${fullPath}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'WebPage',
            '@id': `${getHost(contextLocation)}/merchantapps/appcenter`,
            name: mainTitle
          }
        },
        ...buildChildListItems({
          categorySlug,
          detailsSlug,
          title,
          openGraph,
          contextLocation
        })
      ]
    }
  ];
}

function parseData(stringifiedValue) {
  if (!stringifiedValue) {
    return undefined;
  }

  let result;
  try {
    result = JSON.parse(stringifiedValue);
  } catch (err) {
    return stringifiedValue; // default to string image
  }
  return result;
}

function parseImage(stringifiedValue) {
  // default to image string because some images are not updated with object format in PS now
  const result = parseData(stringifiedValue);
  return result?.medium ? result.medium : result;
}
